import { gsap } from "gsap";

const lerp = (a, b, n) => (1 - n) * a + n * b;

const getMousePos = (e) => ({ x: e.clientX, y: e.clientY });

const distance = (x1, y1, x2, y2) => Math.hypot(x1 - x2, y1 - y2);

let mouse = { x: 0, y: 0 };
window.addEventListener("mousemove", (ev) => {
  mouse = getMousePos(ev);
});

/**
 * Fixed SVG cursor (Codrops MagneticButtons demo 2).
 */
class HeroMagneticCursor {
  constructor(el) {
    this.DOM = { el };
    this.DOM.el.style.opacity = "0";

    this.bounds = this.DOM.el.getBoundingClientRect();

    this.renderedStyles = {
      tx: { previous: 0, current: 0, amt: 0.2 },
      ty: { previous: 0, current: 0, amt: 0.2 },
      scale: { previous: 0, current: 0, amt: 0.2 },
      opacity: { previous: 1, current: 1, amt: 0.2 },
    };

    this.onMouseMoveEv = () => {
      this.renderedStyles.tx.previous = this.renderedStyles.tx.current = mouse.x - this.bounds.width / 2;
      this.renderedStyles.ty.previous = this.renderedStyles.ty.current = mouse.y - this.bounds.height / 2;
      gsap.to(this.DOM.el, { duration: 0.9, ease: "power3.out", opacity: 1 });
      requestAnimationFrame(() => this.render());
      window.removeEventListener("mousemove", this.onMouseMoveEv);
    };
    window.addEventListener("mousemove", this.onMouseMoveEv);
  }

  enter() {
    this.renderedStyles.scale.current = 4;
    this.renderedStyles.opacity.current = 0.2;
  }

  leave() {
    this.renderedStyles.scale.current = 0;
    this.renderedStyles.opacity.current = 1;
  }

  render() {
    this.renderedStyles.tx.current = mouse.x - this.bounds.width / 2;
    this.renderedStyles.ty.current = mouse.y - this.bounds.height / 2;

    for (const key of Object.keys(this.renderedStyles)) {
      const s = this.renderedStyles[key];
      if (s.amt != null) s.previous = lerp(s.previous, s.current, s.amt);
    }

    const { tx, ty, scale, opacity } = this.renderedStyles;
    this.DOM.el.style.transform = `translateX(${tx.previous}px) translateY(${ty.previous}px) scale(${scale.previous})`;
    this.DOM.el.style.opacity = String(opacity.previous);

    requestAnimationFrame(() => this.render());
  }
}

/**
 * Circular magnetic control (demo 2). Viewport coords for scroll/smooth scroll.
 */
class HeroMagneticButton {
  constructor(el, { onEnter, onLeave } = {}) {
    this.onEnterCb = onEnter;
    this.onLeaveCb = onLeave;

    this.DOM = { el };
    this.DOM.text = this.DOM.el.querySelector(".button__text");
    this.DOM.textinner = this.DOM.el.querySelector(".button__text-inner");
    this.DOM.deco = this.DOM.el.querySelector(".button__deco");
    this.DOM.filler = this.DOM.deco.querySelector(".button__deco-filler");

    this.renderedStyles = {
      tx: { previous: 0, current: 0, amt: 0.1 },
      ty: { previous: 0, current: 0, amt: 0.1 },
      scale: { previous: 1, current: 1, amt: 0.2 },
    };

    this.state = { hover: false };

    this.calculateSizePosition();
    this.onResize = () => this.calculateSizePosition();
    window.addEventListener("resize", this.onResize);

    requestAnimationFrame(() => this.render());
  }

  calculateSizePosition() {
    this.rect = this.DOM.el.getBoundingClientRect();
    this.distanceToTrigger = this.rect.width * 1.5;
  }

  render() {
    // Fresh rect every frame — required with smooth scroll (Locomotive/Lenis) or layout shifts
    this.calculateSizePosition();
    const cx = this.rect.left + this.rect.width / 2;
    const cy = this.rect.top + this.rect.height / 2;
    const distanceMouseButton = distance(mouse.x, mouse.y, cx, cy);

    let x = 0;
    let y = 0;

    if (distanceMouseButton < this.distanceToTrigger) {
      if (!this.state.hover) this.enter();
      x = (mouse.x - cx) * 0.3;
      y = (mouse.y - cy) * 0.3;
    } else if (this.state.hover) {
      this.leave();
    }

    this.renderedStyles.tx.current = x;
    this.renderedStyles.ty.current = y;

    for (const key of Object.keys(this.renderedStyles)) {
      const s = this.renderedStyles[key];
      s.previous = lerp(s.previous, s.current, s.amt);
    }

    const { tx, ty, scale } = this.renderedStyles;
    this.DOM.el.style.transform = `translate3d(${tx.previous}px, ${ty.previous}px, 0)`;
    this.DOM.text.style.transform = `translate3d(${-tx.previous * 0.2}px, ${-ty.previous * 0.2}px, 0)`;
    this.DOM.deco.style.transform = `scale(${scale.previous})`;

    requestAnimationFrame(() => this.render());
  }

  enter() {
    this.onEnterCb?.();
    this.state.hover = true;

    this.DOM.el.classList.add("button--hover");

    this.renderedStyles.scale.current = 1.3;

    gsap.killTweensOf(this.DOM.filler);
    gsap.killTweensOf(this.DOM.textinner);

    gsap
      .timeline()
      .fromTo(
        this.DOM.filler,
        { y: "75%" },
        {
          duration: 0.5,
          ease: "power3.out",
          y: "0%",
        },
        0,
      )
      .to(
        this.DOM.textinner,
        {
          duration: 0.4,
          ease: "expo.out",
          scale: 0.8,
        },
        0,
      );
  }

  leave() {
    this.onLeaveCb?.();
    this.state.hover = false;

    this.DOM.el.classList.remove("button--hover");

    this.renderedStyles.scale.current = 1;

    gsap.killTweensOf(this.DOM.filler);
    gsap.killTweensOf(this.DOM.textinner);

    gsap
      .timeline()
      .to(
        this.DOM.filler,
        {
          duration: 0.4,
          ease: "power3.out",
          y: "-75%",
        },
        0,
      )
      .to(
        this.DOM.textinner,
        {
          duration: 0.4,
          ease: "expo.out",
          scale: 1,
        },
        0,
      );
  }
}

export function initHeroScrollTargets() {
  document.querySelectorAll("section.hero-clip, section.hero-caption-img").forEach((section) => {
    const btn = section.querySelector(".hero-magnetic a[data-scroll-to]");
    if (!btn) return;

    let next = section.nextElementSibling;
    while (next && (next.tagName !== "SECTION" || !next.id)) {
      next = next.nextElementSibling;
    }

    if (next?.id) btn.setAttribute("href", `#${next.id}`);
  });
}

export function initMagneticHero() {
  if (window.matchMedia("(max-width: 991px)").matches) return;

  const start = () => {
    const cursorEl = document.querySelector(".hero-magnetic-cursor");
    const buttonEl = document.querySelector(".hero-magnetic .magnetic-button");
    if (!cursorEl || !buttonEl) return;

    const cursor = new HeroMagneticCursor(cursorEl);
    new HeroMagneticButton(buttonEl, {
      onEnter: () => {
        document.body.classList.add("hero-magnetic-active");
        cursor.enter();
      },
      onLeave: () => {
        document.body.classList.remove("hero-magnetic-active");
        cursor.leave();
      },
    });
  };

  // Defer until after first paint so hero layout / smooth-scroll wrappers are stable
  requestAnimationFrame(() => requestAnimationFrame(start));
}
